import {
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  filter,
  first,
  map,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  GetWallDecorsLookBookConfigForPrivateSessionGQL,
  GetWallDecorsLookBookConfigForPublicSessionGQL,
  WallDecorPieceForDisplayType,
  WallDecorSceneForDisplay,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/graphql';
import { ImagesContainer } from 'ZalamoLibrary/src/lib/upload/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { PhotoGroupList } from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import { LookBookPriceRounding } from '../../../../../../../photographer-panel/src/graphql';
import { WallDecorRenderPreviewConfig } from '../../../../../../../photographer-client/src/app/modules/private-sessions/graphql';
import { ImagesInWallDecorMap } from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/types';

import { WallDecorPriceCalculatorService } from '../wall-decor-price-calculator.service';
import { Store } from '@ngrx/store';
import {
  addLookBookProductToBasketAction,
  changeConfigInLookBookProductInBasketAction,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/actions';
import cuid from 'cuid';
import { AutoAddFileToBasketTogetherWithProductService } from '@ui/products/products-front/components/product-card/auto-add-file-to-basket-together-with-product.service';

@Component({
  selector: 'lib-wall-decor-scene-creator',
  templateUrl: './wall-decor-scene-creator.component.html',
  styleUrls: ['./wall-decor-scene-creator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WallDecorSceneCreatorComponent implements OnInit {
  scenePicked = new ReplaySubject<WallDecorSceneForDisplay>(1);
  @Input() context: ImagesContainer;
  @Input() contextId: ImagesContainer;
  @Input() availableImages: string[] = [];
  currentColor$ = new BehaviorSubject(localStorage.wallColor);
  @Input() @Required imageRelations: PhotoGroupList[] = [];
  drawInProgress$ = new Subject<boolean>();
  @Input() rounding: LookBookPriceRounding = LookBookPriceRounding.One;
  @Input() @Required preConfigValue = '';
  preset = new ReplaySubject<WallDecorRenderPreviewConfig>(1);
  currentImages: ImagesInWallDecorMap = null;
  @Input() isUpdate = false;
  @Output() closed = new EventEmitter();
  @Input() currentLookBookConfigId = null;

  @Input() limitPersonalization = false;
  @Input() photoRatio = 1;
  @Output() saved = new EventEmitter<{
    images: ImagesInWallDecorMap;
    wallDecorSceneForDisplayId: string;
  }>();
  currentThumb: {
    fileName: string;
    token: string;
  } = null;
  @Input() type: WallDecorPieceForDisplayType =
    WallDecorPieceForDisplayType.Canvas;

  @Input() set preConfig(prevConfig: WallDecorRenderPreviewConfig) {
    console.log('PRECONFIG', prevConfig);
    this.preset.next(prevConfig);
  }

  constructor(
    private wallDecorPriceCalculatorService: WallDecorPriceCalculatorService,
    private getWallDecorsLookBookConfigForPrivateSession: GetWallDecorsLookBookConfigForPrivateSessionGQL,
    private getWallDecorsLookBookConfigForPublicSessionGQL: GetWallDecorsLookBookConfigForPublicSessionGQL,
    private autoAddFileToBasketTogetherWithProductService: AutoAddFileToBasketTogetherWithProductService,
    private store: Store,
  ) {}

  ngOnInit(): void {}

  private serializeImageMap(currentImages: ImagesInWallDecorMap) {
    return Object.entries(currentImages).map(([pieceId, image]) => ({
      pieceId,
      photoId: image.photoId,
      photoGroupId: image.photoGroupId,
      isFlip: image.isFlip,
      crop: image.crop,
    }));
  }
  save() {
    combineLatest([
      this.scenePicked,
      this.context === ImagesContainer.PrivateSession
        ? this.getWallDecorsLookBookConfigForPrivateSession
            .watch({
              variables: {
                privateSessionId: this.contextId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const {
                  data: { getWallDecorsLookBookConfigForPrivateSession },
                } = completeQueryResult(apolloQueryResult);
                return getWallDecorsLookBookConfigForPrivateSession;
              }),
            )
        : this.getWallDecorsLookBookConfigForPublicSessionGQL
            .watch({
              variables: {
                publicSessionId: this.contextId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const {
                  data: { getWallDecorLookBookConfigForPublicSession },
                } = completeQueryResult(apolloQueryResult);
                return getWallDecorLookBookConfigForPublicSession;
              }),
            ),
      this.currentColor$,
    ])
      .pipe(
        switchMap(([scene, config, color]) =>
          this.wallDecorPriceCalculatorService
            .calculatePrice(
              scene.pieces,
              config,
              this.rounding,
              this.preConfigValue,
            )
            .pipe(
              map((price) => ({
                scene,
                price,
                color,
              })),
            ),
        ),
        tap(({ scene, price, color }) => {
          const currentImages = { ...this.currentImages };
          const pieces = scene.pieces.map(
            ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
          );
          for (const currentPiece in currentImages) {
            if (!pieces.includes(currentPiece)) {
              delete currentImages[currentPiece];
            }
          }
          const usedPhotos = Object.values(currentImages);
          if (!this.isUpdate) {
            this.store.dispatch(
              addLookBookProductToBasketAction({
                lookBookProduct: {
                  config: JSON.stringify({
                    sceneId: scene.wallDecorSceneForDisplayId,
                    color,
                  }),
                  price: {
                    min: [{ price, code: 'PLN' }],
                    max: [{ price, code: 'PLN' }],
                  },
                  lookBookProductId:
                    this.type === WallDecorPieceForDisplayType.Canvas
                      ? 'cl52b57vg00fz359poteq53r0'
                      : 'cl8cpxj79000635a07vjh62in',
                  countPhotos: {
                    min: scene.pieces.length,
                    max: scene.pieces.length,
                  },
                  redBackground:
                    Object.keys(currentImages).length !== scene.pieces.length,
                  finished:
                    Object.keys(currentImages).length === scene.pieces.length,
                  lookBookProductConfigId: cuid(),
                  productsInPlay: [],
                  name: [
                    {
                      text:
                        this.type === WallDecorPieceForDisplayType.Canvas
                          ? 'Wall Decor'
                          : 'Fototamborek',
                      language: 'pl',
                    },
                  ],
                  firstAddedFile: false,
                  hasPersonalization: false,
                  nameClientProduct: '',
                  personalizationConfig: null,
                  photoIdList: usedPhotos.map(({ photoId }) => photoId),
                  quantity: 1,
                  thumb: this.currentThumb.fileName,
                  accessToken: this.currentThumb.token,
                  valueMap: JSON.stringify(currentImages),
                  imageMap: this.serializeImageMap(currentImages),
                },
              }),
            );
          } else {
            this.store.dispatch(
              changeConfigInLookBookProductInBasketAction({
                product: {
                  config: JSON.stringify({
                    color,
                    sceneId: scene.wallDecorSceneForDisplayId,
                  }),
                  price: {
                    min: [{ price, code: 'PLN' }],
                    max: [{ price, code: 'PLN' }],
                  },
                  countPhotos: {
                    min: scene.pieces.length,
                    max: scene.pieces.length,
                  },
                  redBackground:
                    Object.keys(currentImages).length !== scene.pieces.length,
                  finished:
                    Object.keys(currentImages).length === scene.pieces.length,
                  lookBookProductConfigId: this.currentLookBookConfigId,
                  photoIdList: usedPhotos.map(({ photoId }) => photoId),
                  thumb: this.currentThumb.fileName,
                  accessToken: this.currentThumb.token,
                  valueMap: JSON.stringify(currentImages),
                  imageMap: this.serializeImageMap(currentImages),
                  productsInPlay: [],
                  hasPersonalization: false,
                  personalizationConfig: null,
                },
              }),
            );
          }
          this.autoAddFileToBasketTogetherWithProductService.autoAddFileToBasketTogetherWithLookBookProduct();
          console.log(scene, price, this.currentImages);
          this.closed.next(undefined);
        }),
        first(),
      )
      .subscribe();
  }
}
